/**
 * Pure utility functions for resolving community/member assignments
 * based on changelog entries and a reference date.
 *
 * These utilities support the v1_4 /entities response which adds a
 * `changelog` array to site and member objects.
 */

interface ChangelogEntry {
  startDate: string;
  endDate: string;
  [key: string]: any;
}

/**
 * Find the changelog entry that covers a given date.
 * Uses string comparison (ISO yyyy-MM-dd format).
 * Returns the matching entry or null.
 */
export function resolveAtDate<T extends ChangelogEntry>(
  changelog: T[] | undefined | null,
  dateFrom: string
): T | null {
  if (!changelog || !Array.isArray(changelog) || !dateFrom) return null;

  return (
    changelog.find(
      (entry) => entry.startDate <= dateFrom && entry.endDate >= dateFrom
    ) ?? null
  );
}

/**
 * Resolve the communityId for a site at a given date using its changelog.
 * Falls back to `site.communityId` if changelog is absent or has no match.
 */
export function resolveCommunityForSite(site: any, dateFrom: string): string | null {
  if (!site) return null;

  const entry = resolveAtDate(site.changelog, dateFrom);
  if (entry?.communityId != null) return entry.communityId;

  return site.communityId ?? null;
}

/**
 * Resolve the communityId for a member at a given date using its changelog.
 * Falls back to `member.communityId` if changelog is absent or has no match.
 */
export function resolveCommunityForMember(member: any, dateFrom: string): string | null {
  if (!member) return null;

  const entry = resolveAtDate(member.changelog, dateFrom);
  if (entry?.communityId != null) return entry.communityId;

  return member.communityId ?? null;
}

/**
 * FLEXO-6114: resolve the community CONTEXT for a member, honouring the
 * community the user navigated from.
 *
 * Rules (in order):
 *  1. `fromCommunity` — the community the navigation started from — wins, but
 *     ONLY if the member is actually active in it at `dateFrom` (so changing
 *     the calendar date out of that period correctly falls back to the
 *     changelog, and a stale/foreign `fromCommunity` never sticks).
 *  2. otherwise the first changelog entry active at `dateFrom`
 *     (== resolveCommunityForMember behaviour).
 *  3. otherwise the flat `member.communityId` scalar (deep-link fallback).
 *
 * pcc-clean: changelog entries whose site is filtered out of the entities
 * payload (`siteId ∉ visibleSiteIds` — the proxy for pcc/pccv hidden by the
 * backend SiteFilter) are ignored, so a member's community context mirrors the
 * VISIBLE member lists. `visibleSiteIds` is optional — when omitted the pcc
 * filter is skipped (callers that lack the site set stay on legacy behaviour).
 */
export function resolveContextCommunity(
  member: any,
  opts: {
    fromCommunity?: string | null;
    dateFrom: string;
    visibleSiteIds?: Set<string> | null;
  }
): string | null {
  if (!member) return null;
  const { fromCommunity, dateFrom, visibleSiteIds } = opts;

  const changelog = Array.isArray(member.changelog) ? member.changelog : [];

  const activeVisible = changelog.filter((e: any) => {
    if (!e?.communityId) return false;
    const started = !e.startDate || e.startDate <= dateFrom;
    const notEnded = !e.endDate || e.endDate >= dateFrom;
    if (!started || !notEnded) return false;
    // pcc-clean: drop entries pointing at a filtered (pcc/pccv) site.
    if (visibleSiteIds && e.siteId && !visibleSiteIds.has(e.siteId)) return false;
    return true;
  });

  const activeCommunityIds = new Set<string>(
    activeVisible.map((e: any) => e.communityId)
  );

  // (1) source community, only if valid at this date (and visible).
  if (fromCommunity && activeCommunityIds.has(fromCommunity)) {
    return fromCommunity;
  }

  // (2) first active-visible changelog community.
  if (activeVisible.length > 0) {
    return activeVisible[0].communityId;
  }

  // (3) legacy fallback: date-resolved changelog (no visibility filter) then scalar.
  return resolveCommunityForMember(member, dateFrom);
}

/**
 * Compute the navigation date params when navigating from a community to a member.
 * If the member has left the community, returns the last day they were in it.
 * If the member is still in the community, returns null (no special params needed).
 */
export function computeNavigationDatesForMemberInCommunity(
  member: any,
  communityId: string
): { dateFrom: string; dateTo: string; timeSet: string } | null {
  if (!member?.changelog || !Array.isArray(member.changelog) || !communityId) return null;

  const entries = member.changelog.filter((e: any) => e.communityId === communityId);
  if (entries.length === 0) return null;

  // Use the most recent period in this community
  const entry = entries.sort((a: any, b: any) =>
    (b.endDate || '').localeCompare(a.endDate || '')
  )[0];

  // If still in this community (FAR_FUTURE 2099-xx-xx), no special dates needed
  if (entry.endDate >= '2099-01-01') return null;

  // Member has left this community — navigate to the last day
  const endDate = entry.endDate;
  const endDateObj = new Date(endDate + 'T00:00:00');
  endDateObj.setDate(endDateObj.getDate() + 1);
  const year = endDateObj.getFullYear();
  const month = String(endDateObj.getMonth() + 1).padStart(2, '0');
  const day = String(endDateObj.getDate()).padStart(2, '0');

  return {
    dateFrom: endDate,
    dateTo: `${year}-${month}-${day}`,
    timeSet: 'day',
  };
}

/**
 * Filter invoices so that only those whose billing period overlaps with
 * the site's assignment to `communityId` (per changelog) are kept.
 *
 * Uses a multi-layered approach:
 * 1. Changelog-based overlap + secondary check (existing logic)
 * 2. Cross-reference: invoice userId + memberChangelog + changelog to verify
 *    the invoiced user was active during this community's assignment (safety net)
 * 3. Hardened fallback with joinDate/leaveDate bounds when no changelog
 */
export function filterInvoicesByCommunityPeriod(
  invoices: any[],
  communityId: string,
  sites: any[]
): any[] {
  if (!invoices || !Array.isArray(invoices)) return [];
  if (!communityId) return invoices;

  const sitesMap = new Map<string, any>();
  if (sites && Array.isArray(sites)) {
    for (const site of sites) {
      if (site.siteId) sitesMap.set(site.siteId, site);
    }
  }

  return invoices.filter((invoice) => {
    const siteId = invoice.siteId || invoice.siteID || invoice.site_id;
    if (!siteId) return true;

    const site = sitesMap.get(siteId);
    if (!site) return false;

    const periodStart = invoice.period_start_date;
    const periodEnd = invoice.period_end_date;

    // If invoice has no period dates, include it for safety
    if (!periodStart && !periodEnd) return true;

    // If site has changelog, check overlap with community entries
    if (site.changelog && Array.isArray(site.changelog)) {
      const communityEntries = site.changelog.filter(
        (entry: any) => entry.communityId === communityId
      );

      if (communityEntries.length > 0) {
        const passesOverlap = communityEntries.some((entry: any) => {
          const entryStart = entry.startDate;
          const entryEnd = entry.endDate;
          // Overlap check: NOT (entry ends before invoice starts OR entry starts after invoice ends)
          if (periodStart && entryEnd && entryEnd < periodStart) return false;
          if (periodEnd && entryStart && entryStart > periodEnd) return false;

          // If this community started after the invoice period began, check if
          // another community was active at the start of the invoice period.
          // If so, the invoice belongs to that earlier community, not this one.
          if (periodStart && periodEnd && entryStart && entryStart > periodStart) {
            const otherActiveAtPeriodStart = site.changelog.some(
              (other: any) =>
                other.communityId !== communityId &&
                other.startDate <= periodStart &&
                (!other.endDate || other.endDate >= periodStart)
            );
            if (otherActiveAtPeriodStart) return false;
          }

          return true;
        });

        if (!passesOverlap) return false;

        // Safety net: cross-reference invoice userId with memberChangelog + changelog.
        // If the invoice's user was only active during a DIFFERENT community's period, exclude.
        const invoiceUserId = invoice.userId || invoice.user_id;
        if (
          invoiceUserId &&
          site.memberChangelog && Array.isArray(site.memberChangelog) &&
          site.memberChangelog.length > 0
        ) {
          const userEntries = site.memberChangelog.filter(
            (e: any) => e.memberId === invoiceUserId
          );

          if (userEntries.length > 0) {
            // Check if ANY of the user's ownership periods overlap with
            // this community's assignment period
            const userOverlapsCommunity = userEntries.some((userEntry: any) =>
              communityEntries.some((commEntry: any) => {
                if (userEntry.endDate && commEntry.startDate && userEntry.endDate < commEntry.startDate) return false;
                if (userEntry.startDate && commEntry.endDate && userEntry.startDate > commEntry.endDate) return false;
                return true;
              })
            );
            if (!userOverlapsCommunity) return false;
          }
        }

        return true;
      }
    }

    // Hardened fallback: use current assignment + joinDate bounds
    if (site.communityId !== communityId) return false;
    const joinDate = site.joinDate || site.startDate;
    if (joinDate && periodEnd && periodEnd < joinDate) return false;
    return true;
  });
}

/**
 * Filter invoices so that only those belonging to the given member are kept.
 *
 * Primary check: invoice.userId must match the memberId (direct attribution).
 * Secondary check (when userId is missing): date overlap with memberChangelog.
 * Fallback: site.memberId === memberId (current assignment).
 */
export function filterInvoicesByMemberPeriod(
  invoices: any[],
  memberId: string,
  sites: any[]
): any[] {
  if (!invoices || !Array.isArray(invoices)) return [];
  if (!memberId) return invoices;

  const sitesMap = new Map<string, any>();
  if (sites && Array.isArray(sites)) {
    for (const site of sites) {
      if (site.siteId) sitesMap.set(site.siteId, site);
    }
  }

  return invoices.filter((invoice) => {
    // Primary check: if invoice has a userId, it directly identifies the owner
    const invoiceUserId = invoice.userId || invoice.user_id;
    if (invoiceUserId) {
      return invoiceUserId === memberId;
    }

    // If no userId on invoice, fall back to date-based filtering
    const siteId = invoice.siteId || invoice.siteID || invoice.site_id;
    if (!siteId) return true;

    const site = sitesMap.get(siteId);
    if (!site) return false;

    const periodStart = invoice.period_start_date;
    const periodEnd = invoice.period_end_date;

    // If invoice has no period dates, include it for safety
    if (!periodStart && !periodEnd) return true;

    // If site has memberChangelog, check overlap with member entries
    if (site.memberChangelog && Array.isArray(site.memberChangelog)) {
      const memberEntries = site.memberChangelog.filter(
        (entry: any) => entry.memberId === memberId
      );

      if (memberEntries.length > 0) {
        return memberEntries.some((entry: any) => {
          const entryStart = entry.startDate;
          const entryEnd = entry.endDate;
          // Overlap check: NOT (entry ends before invoice starts OR entry starts after invoice ends)
          if (periodStart && entryEnd && entryEnd < periodStart) return false;
          if (periodEnd && entryStart && entryStart > periodEnd) return false;
          return true;
        });
      }
    }

    // Fallback: no memberChangelog or no matching entries → use current assignment
    return site.memberId === memberId;
  });
}

/**
 * Reverse lookup: find the memberId that owned a given siteId at a given date.
 * Searches each member's changelog for an entry with matching siteId + date.
 * Falls back to flat structure (member.sites) if no changelog match is found.
 */
export function resolveMemberForSite(
  allMembers: any[],
  siteId: string,
  dateFrom: string
): string | null {
  if (!allMembers || !siteId) return null;

  // Try changelog-based lookup first
  for (const member of allMembers) {
    if (!member?.changelog || !Array.isArray(member.changelog)) continue;

    const match = member.changelog.find(
      (entry: any) =>
        entry.siteId === siteId &&
        entry.startDate <= dateFrom &&
        entry.endDate >= dateFrom
    );

    if (match) return member.memberId;
  }

  // Fallback: flat structure (member.sites populated by structureData)
  for (const member of allMembers) {
    if (!member?.sites || !Array.isArray(member.sites)) continue;

    if (member.sites.some((site: any) => site.siteId === siteId)) {
      return member.memberId;
    }
  }

  return null;
}
